import React from 'react';
import { Table, Button, Space, Tag, Avatar, Image } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, EyeInvisibleOutlined, PictureOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { format } from 'date-fns';

const GalleryTable = ({ galleryImages, onEdit, onDelete, onToggleStatus }) => {
  if (!galleryImages || galleryImages.length === 0) {
    return null; // Handled by parent component
  }

  const columns = [
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Gallery Media</span>,
      key: 'media',
      render: (_, image) => {
        const mediaTitle = image?.title || 'Untitled';
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {image.mediaType === 'photo' ? (
              <Image
                src={image.mediaUrl}
                alt={mediaTitle}
                width={40}
                height={40}
                style={{ 
                  objectFit: 'cover',
                  borderRadius: '4px'
                }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='100%25' height='100%25' fill='%23e6d5f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23470172' font-size='12'%3EImg%3C/text%3E%3C/svg%3E"
              />
            ) : (
              <Avatar 
                size={40} 
                icon={<VideoCameraOutlined />}
                style={{ 
                  backgroundColor: '#e6d5f0', 
                  color: '#470172',
                }}
              />
            )}
            <div>
              <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                {mediaTitle}
              </div>
              <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400, color: '#8b8b8b' }}>
                {image?.description?.substring(0, 60) || 'No description'}...
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Type</span>,
      key: 'type',
      responsive: ['md'],
      render: (_, image) => (
        <Tag 
          icon={image.mediaType === 'photo' ? <PictureOutlined /> : <VideoCameraOutlined />}
          color={image.mediaType === 'photo' ? 'blue' : 'purple'}
          style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400 }}
        >
          {image.mediaType === 'photo' ? 'Photo' : 'Video'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Status</span>,
      key: 'status',
      responsive: ['md'],
      render: (_, image) => (
        <Tag 
          color={image.isActive ? 'success' : 'default'}
          style={{ fontFamily: 'Rubik, sans-serif', fontSize: '14px', fontWeight: 400 }}
        >
          {image.isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Created</span>,
      key: 'created',
      responsive: ['lg'],
      render: (_, image) => (
        <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
          {image?.createdAt ? format(new Date(image.createdAt), 'MMM d, yyyy') : '—'}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Actions</span>,
      key: 'actions',
      align: 'right',
      render: (_, image) => (
        <Space size="small">
          <Button
            type="text"
            icon={image.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => onToggleStatus(image._id)}
            style={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '16px',
              fontWeight: 400,
              color: '#470172',
            }}
            title={image.isActive ? 'Deactivate' : 'Activate'}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(image)}
            style={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '16px',
              fontWeight: 400,
              color: '#470172',
            }}
            title="Edit"
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(image._id)}
            style={{
              fontFamily: 'Rubik, sans-serif',
              fontSize: '16px',
              fontWeight: 400,
            }}
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={galleryImages}
      rowKey="_id"
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            Total {total} items
          </span>
        ),
        style: { fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400 }
      }}
      style={{ fontFamily: 'Rubik, sans-serif' }}
      locale={{
        emptyText: (
          <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
            No gallery media found
          </span>
        )
      }}
      rowClassName={() => 'hover:bg-gray-50'}
    />
  );
};

export default GalleryTable;
